import type { Sprite } from "./cast";
import { type Ctx, cyc, ptext, R } from "./pixel";

/** How hurried the travelling character is; speeds up the car animation. */
export type PipUrgency = "high" | "low" | undefined;

export type PipSceneFn = (c: Ctx, n: number, sp: Sprite, urg: PipUrgency) => void;

/** 96×54 picture-in-picture vignettes (e.g. 林主任 waking up and driving in). */
export const PIP: Record<string, PipSceneFn> = {
  sleep(c, n, sp) {
    R(c, "#0c1230", 0, 0, 96, 54);
    R(c, "#1b2450", 0, 42, 96, 12);
    R(c, "#3a3f6a", 18, 32, 60, 10);
    R(c, "#e8e8f0", 20, 29, 14, 5);
    c.save();
    c.translate(32, 32);
    c.rotate(-Math.PI / 2);
    c.drawImage(sp.f0, -8, -8);
    c.restore();
    R(c, "#5a6aa8", 34, 29, 42, 8);
    if (Math.floor(n / 500) % 2) {
      R(c, "#9fd0ef", 78, 30, 4, 3);
    }
    ptext(c, "Z", 42 + (Math.floor(n / 400) % 3), 14, "#b8c2f0");
  },
  wake(c, n, sp) {
    R(c, "#141a3a", 0, 0, 96, 54);
    R(c, "#22285a", 0, 42, 96, 12);
    R(c, "#3a3f6a", 14, 34, 48, 8);
    R(c, "#5a6aa8", 30, 32, 30, 6);
    const ox = Math.floor(n / 120) % 2;
    c.drawImage(sp.f0, 28 + ox, 18);
    R(c, "#5b4636", 66, 30, 14, 12);
    R(c, "#2c2c3e", 70, 28, 6, 2);
    R(c, "#ffd447", 6, 6, 8, 8);
  },
  dress(c, _n, sp) {
    R(c, "#3a3050", 0, 0, 96, 54);
    R(c, "#4a405e", 0, 42, 96, 12);
    R(c, "#6b4a34", 64, 6, 22, 36);
    R(c, "#8a5f40", 66, 8, 8, 32);
    R(c, "#8a5f40", 76, 8, 8, 32);
    c.drawImage(sp.f0, 38, 24);
    R(c, "#e05a6a", 38, 40, 4, 2);
    R(c, "#5f8fb8", 48, 40, 4, 2);
  },
  brush(c, n, sp) {
    R(c, "#cfe3ef", 0, 0, 96, 54);
    for (let x = 0; x < 96; x += 8) R(c, "#b8d2e2", x, 0, 1, 54);
    R(c, "#9fd0ef", 34, 4, 28, 16);
    R(c, "#e8e8f0", 30, 34, 36, 8);
    c.drawImage(sp.f0, 40, 22);
    R(c, "#ffd447", 54, 30 + (Math.floor(n / 150) % 2), 7, 1);
  },
  coffee(c, _n, sp) {
    R(c, "#f4f4f6", 0, 0, 96, 54);
    for (let i = 0; i < 3; i++) {
      R(c, "#9aa0b2", 4, 8 + i * 12, 30, 2);
      R(c, cyc(["#e0a3a3", "#a3c2e0", "#e8d48a"], i), 6, 2 + i * 12, 24, 6);
    }
    R(c, "#c8463e", 60, 30, 32, 14);
    R(c, "#20263a", 64, 24, 10, 6);
    c.drawImage(sp.f0, 42, 24);
    R(c, "#8a5f40", 56, 32, 3, 4);
  },
  car(c, n, _sp, urg) {
    R(c, "#0b1024", 0, 0, 96, 54);
    for (let i = 0; i < 4; i++) {
      const x = ((i * 30 - (Math.floor(n / (urg === "high" ? 8 : 20)) % 30) + 120) % 120) - 12;
      R(c, "#5a6070", x, 6, 2, 28);
      R(c, "#ffd447", x - 2, 4, 6, 3);
    }
    R(c, "#2a2e40", 0, 34, 96, 20);
    const off = Math.floor(n / (urg === "high" ? 20 : 50)) % 16;
    for (let x = -16 + off * -1; x < 96; x += 16) R(c, "#e8e8a0", x + ((off * 2) % 16), 43, 8, 2);
    R(c, "#c8463e", 28, 26, 40, 10);
    R(c, "#a8323a", 36, 20, 22, 7);
    R(c, "#9fd0ef", 38, 21, 18, 5);
    R(c, "#b9b9c4", 44, 21, 6, 3);
    R(c, "#ecc19a", 45, 23, 4, 2);
    R(c, "#1b1426", 32, 35, 8, 4);
    R(c, "#1b1426", 56, 35, 8, 4);
    R(c, "#fff2a0", 66, 28, 4, 3);
  },
  park(c, n, sp) {
    R(c, "#3a3f52", 0, 0, 96, 54);
    for (let x = 4; x < 96; x += 20) R(c, "#e8e8f0", x, 30, 2, 24);
    R(c, "#5f86bf", 8, 34, 16, 10);
    R(c, "#c8463e", 48, 34, 16, 10);
    const x = (Math.floor(n / 30) % 110) - 14;
    c.drawImage(Math.floor(n / 140) % 2 ? sp.f1 : sp.f0, x, 12);
  },
  elev(c, n, _sp) {
    R(c, "#7e88b8", 0, 0, 96, 54);
    R(c, "#9aa0b2", 24, 8, 48, 46);
    R(c, "#7a8092", 47, 8, 2, 46);
    R(c, "#1b1426", 38, 0, 20, 7);
    ptext(c, String(1 + (Math.floor(n / 1500) % 7)) + "F", 42, 1, "#ff5a5a");
  },
  run(c, n, sp) {
    R(c, "#e9e1cf", 0, 0, 96, 54);
    R(c, "#7e88b8", 0, 0, 96, 14);
    R(c, "#d8cdb5", 0, 30, 96, 1);
    const x = (Math.floor(n / 10) % 120) - 16;
    c.drawImage(Math.floor(n / 100) % 2 ? sp.f1 : sp.f0, x, 24);
  },
  clinic(c, _n, sp) {
    R(c, "#e3efe4", 0, 0, 96, 54);
    R(c, "#b98552", 30, 30, 36, 8);
    c.drawImage(sp.f0, 40, 16);
    R(c, "#1b1426", 70, 4, 22, 10);
  },
};
